import React, { useEffect, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { HelpBreadcrumbs, getArticleHeadings, getSectionTitle, useHelpNode } from '@ergeon/help-components';

import { authService } from 'common/auth';
import NotFoundPage from 'components/NotFoundPage';

import { WIKI_API_HOST, WIKI_DOMAIN, WIKI_TITLE } from '../constants';
import { useShowCurrentNode } from '../CurrentNodeProvider';
import { WikiNode } from '../types';
import WikiMeta from '../WikiMeta';
import { useWikiPalette } from '../WikiPaletteProvider';

import { Heading } from './headings';
import NodeBody from './NodeBody';
import NodeRail from './NodeRail';

const useRememberViewedArticle = (node: WikiNode | null) => {
  const { rememberArticle } = useWikiPalette();

  useEffect(function rememberTheOpenedArticle() {
    if (node) {
      rememberArticle({ nodeKey: node.nodeKey, section: getSectionTitle(node.breadcrumb, ''), title: node.title });
    }
  }, [node, rememberArticle]);
};

const WikiNodePage = () => {
  const { nodeKey } = useParams();
  const navigate = useNavigate();
  const helpNode = useHelpNode({ apiHost: WIKI_API_HOST, auth: authService, domain: WIKI_DOMAIN, nodeKey });
  const node: WikiNode | null = helpNode.node;
  const headings: Heading[] = useMemo(() => getArticleHeadings(node?.content), [node]);

  useShowCurrentNode(node);
  useRememberViewedArticle(node);

  if (helpNode.isNotFound) {
    return <NotFoundPage />;
  }

  return (
    <>
      <WikiMeta pageTitle={node?.title} />
      <div className="mx-auto flex w-full max-w-6xl gap-10 px-4 py-8 md:px-8 md:py-10">
        <div className="flex min-w-0 max-w-3xl flex-1 flex-col gap-6">
          <HelpBreadcrumbs
            breadcrumb={node?.breadcrumb}
            currentTitle={node?.title}
            homeLabel={WIKI_TITLE}
            onNavigate={navigate} />
          <NodeBody headings={headings} helpNode={helpNode} />
        </div>
        <aside aria-label="Page details" className="hidden w-64 shrink-0 xl:block">
          <NodeRail headings={headings} node={node} />
        </aside>
      </div>
    </>
  );
};

export default WikiNodePage;
